import { Base, Component, Prop } from '@/vue-property-decorator'
import { Bind, Debounce } from '@/utils/Decorator'
import { CanClickWhenForceBind } from '@/views/center/security/store'
import { Currency } from '@/config/currencies.config'
import { CurrencyEnum, CurrencyInfos } from '@/api/common/type'
import { Dropdown, Icon, Row } from '@/plugins/ant-design-vue/tsx-support'
import { GlobalConst, GlobalEvents, GlobalManager } from '@/context'
import { GlobalEventActions } from '@/context/events'
import { createSvgSprite } from '@/utils/business-utils/assets'
import { useI18n } from '@/i18n'
import { useMainStore } from '@/store/index'
import AppTypes from '@/vue-types'
import Skin from '@/controller/Skin'
import SwitchCurrencyOptions from '@/components/business-components/switch-currency/options'
import style from './style.module.scss'

interface Props {
  getPopupContainer?: (triggerNode?: HTMLElement) => HTMLElement
  width?: number
  /**
   * 语言展开模式（下拉居中，右侧居上）
   */
  placement?: 'bottomCenter' | 'rightTop'
  /**
   * 禁止选择语言
   */
  disabled?: boolean
  /** 后缀图标 */
  suffixArrow?: VueTsxSupport.JSX.Element | (() => VueTsxSupport.JSX.Element)

  // icon 尺寸
  iconSize?: number
}

interface State {
  loading: boolean
  visible: boolean
}

@Component<SwitchCurrency>({
  name: 'SwitchCurrency'
})
export default class SwitchCurrency extends Base<State, Props> {
  @Prop(AppTypes.number)
  readonly width!: Props['width']
  @Prop(AppTypes.string)
  readonly placement!: Props['placement']
  @Prop({ required: false })
  getPopupContainer!: Props['getPopupContainer']
  @Prop(AppTypes.bool.def(false))
  readonly disabled!: Props['disabled']
  @Prop({ default: null })
  readonly suffixArrow?: Props['suffixArrow']
  @Prop(AppTypes.number)
  readonly iconSize!: Props['iconSize']

  state = {
    loading: false,
    visible: false
  }

  @Bind()
  @Debounce()
  private async updateCurrency(e: Event) {
    e.stopPropagation()
    e.stopImmediatePropagation()
    const { updatePrize } = useMainStore()
    await updatePrize()
  }
  @Bind()
  @Debounce(50)
  public switchPopover() {
    this.setState({
      visible: !this.state.visible
    })
  }

  private get isWeb() {
    return useMainStore()?.isWeb
  }

  private get tryCurrencyConfig() {
    const { siteInfos } = useMainStore()
    return {
      /**
       * 当前站点货币列表
       */
      currencyInfos: siteInfos?.currencyInfos || []
    }
  }
  private get singleCurrency() {
    return this.tryCurrencyConfig.currencyInfos?.length <= 1
  }
  /**选项列表*/
  private renderCurrencyList(isModal?: boolean) {
    return (
      <SwitchCurrencyOptions
        class={this.state.visible ? 'currencyPopupOpen' : 'currencyPopupClose'}
        showIcon={isModal}
        onSelected={() => {
          this.setState({ visible: false })
        }}
        onLoadState={(load = false) => {
          this.setState({ loading: load })
        }}
      />
    )
  }

  /**列表内单个选项内容
   * currency 币种数据
   * showIcon 是否展示尾部icon
   * */
  private renderItem(params: {
    currency: CurrencyInfos
    showIconBg?: boolean
    showIcon?: boolean
    showName?: boolean
  }) {
    const { currency, showIcon, showName } = params
    const currencyIconMap = Currency.registerIconMap()
    const { isWeb, language } = useMainStore()
    const { t } = useI18n()

    const currencyItem =
      currencyIconMap[
        (currency.currencyType === CurrencyEnum.CRYPTO
          ? currency.currencyDisplay
          : currency.currencyCode) as unknown as keyof typeof currencyIconMap
      ]

    const icon = currencyItem?.icon
    const width = isWeb ? 28 : 36
    const scale = this.iconSize
      ? this.iconSize / icon?.width
      : width / icon?.width
    return (
      <Row.Tsx
        title={''}
        class={[style.item, CanClickWhenForceBind]}
        type={'flex'}
        align={'middle'}
      >
        <my-img
          class={[style['icon-box-head'], 'switch-lang__icon']}
          src={'/lobby_asset/common/common/_sprite/img_hb_frame.png'}
          options={{
            type: 'div'
          }}
        >
          <icon-sprite useSkeleton scale={scale} sprite={icon} />
        </my-img>

        {currency.currencyType === CurrencyEnum.CRYPTO ? (
          <span
            class={{
              [GlobalConst.CssClassName.Lobby.Ellipsis]: true,
              'switch-lang__text': true
            }}
          >
            {language === 'zh_CN' &&
              currency.marketCurrencyCode.includes('BTC') &&
              t('lobby.modal.currency.BTC')}
            {language === 'zh_CN' &&
              currency.marketCurrencyCode.includes('ETH') &&
              t('lobby.modal.currency.ETH')}
            {currency.currencyDisplay}
            {showName ? `（${currencyItem?.title}）` : ''}
          </span>
        ) : (
          <span
            class={{
              [GlobalConst.CssClassName.Lobby.Ellipsis]: true,
              'switch-lang__text': true
            }}
          >
            {showName
              ? `${currencyItem?.title}（${currency?.currencyCode}）`
              : currency?.currencyCode}
          </span>
        )}

        {showIcon && (
          <div
            class={{
              [style.cicle]: true,
              ['cicle']: true
            }}
          >
            <icon-sprite
              useSkeleton
              sprite={createSvgSprite('comm_icon_gou')}
            />
          </div>
        )}
      </Row.Tsx>
    )
  }

  private handlerOpenCurrencyModal() {
    GlobalManager.Modal.open('switchCurrencyPopup')
  }

  // 条件为欧美版、H5模式、设置的试玩币种只有一种，需要隐藏下拉事件和样式
  private get isHiddenDownArrow() {
    const { isEuropeanAmerican } = Skin.currentTheme()
    return (
      isEuropeanAmerican &&
      this.tryCurrencyConfig.currencyInfos.length === 1 &&
      !this.isWeb
    )
  }

  /**组件基础外观*/
  private renderCurrencyInfo() {
    const { userInfos } = useMainStore()
    const { isStake } = Skin.currentTheme()
    const curr = this.tryCurrencyConfig.currencyInfos.find(
      (it) => it?.currencyCode == userInfos?.currency
    )
    if (curr) {
      return (
        <div
          class={[
            style['current-container'],
            'current-container',
            this.state.visible
              ? 'currencyCurrentPopupOpen'
              : 'currencyCurrentPopupClose'
          ]}
        >
          {this.renderItem({
            currency: curr || [],
            showIcon: false,
            showName: isStake && this.isWeb ? true : false
          })}
          {this.isHiddenDownArrow ? null : (
            <Icon.Tsx
              class={[
                style['right-icon'],
                'switch-currency__arrow',
                style['header-right-icon']
              ]}
              component={{
                functional: true,
                render: () => {
                  if (this.state.loading) {
                    return <Icon.Tsx type={'loading'} />
                  }
                  return this.suffixArrow ? (
                    typeof this.suffixArrow == 'function' ? (
                      this.suffixArrow()
                    ) : (
                      this.suffixArrow
                    )
                  ) : (
                    <icon-sprite
                      class={[style.arrow, 'arrow']}
                      sprite={createSvgSprite('comm_icon_sort')}
                      useSkeleton
                      attrs={{
                        'data-visible': this.state.visible
                      }}
                    />
                  )
                }
              }}
            />
          )}
        </div>
      )
    }
  }

  @Bind()
  closeDropdown(action: GlobalEventActions) {
    if (action.type === 'CLOSE_DROPDOWN' && this.state.visible) {
      this.setState({
        visible: false
      })
    }
  }

  created() {
    GlobalEvents.subscribe(this.closeDropdown)
  }

  render() {
    const account_type = useMainStore().userInfos?.account_type || 1
    // 未登录的试玩 当只是配置一种币种的时候则不需要展示
    if (this.singleCurrency && account_type === 1) {
      return <div></div>
    }

    //h5点击币种是弹窗式列表。web是展开下拉选
    if (!this.isWeb) {
      return (
        <div
          class={[style['currency-switch-header'], 'currency-switch-header']}
          onClick={() => {
            if (this.disabled) {
              return
            }
            !this.isHiddenDownArrow && this.handlerOpenCurrencyModal()
          }}
        >
          {this.renderCurrencyInfo()}
        </div>
      )
    }
    return (
      <div class={style['currency-switch-header']}>
        <Dropdown.Tsx
          class={[CanClickWhenForceBind]}
          trigger={['click']}
          placement={'bottomLeft'}
          v-model={this.state.visible}
          overlayClassName={style.languagePopup}
          getPopupContainer={this?.getPopupContainer}
        >
          <template slot="overlay">
            <my-scroll direction="y">{this.renderCurrencyList()}</my-scroll>
          </template>
          {this.renderCurrencyInfo()}
        </Dropdown.Tsx>
      </div>
    )
  }
}
